import { useState, useCallback, useEffect } from 'react'
import * as ComplexParcelDetailsLayer from '../../map/layers/ComplexParcelDetailsLayer'
import { Detail } from '../types'
import { groupBy } from '../../util'
import { ParcelDetailType } from '../components/ComplexParcelDetail'


export type DetailsGroup = Detail[]

export const groupAndFilterComplexDetails = (details: Detail[], totalArea: number): DetailsGroup[] => {
  const groupedDetails = groupBy(details, (d) => d.label) as Record<string, DetailsGroup>;
  const filteredDetailGroups = Object.values(groupedDetails).filter((detailsGroup) => {
    const groupArea = detailsGroup.reduce((accum, detail) => accum + detail.area, 0);
    return groupArea / totalArea > 0.005
  })
  filteredDetailGroups.sort((a, b) => {
    const aArea = a.reduce((accum, detail) => accum + detail.area, 0);
    const bArea = b.reduce((accum, detail) => accum + detail.area, 0);
    return aArea > bArea ? -1 : 1
  });

  return filteredDetailGroups
}



const highlightDetail = (details: Detail[], PIN: number, type: ParcelDetailType) => {
  const handle = ComplexParcelDetailsLayer.addDetailPolygons(details, PIN, type)
  return handle
}

const unhighlightDetail = (handle: number) => {
  ComplexParcelDetailsLayer.removeDetailPolygon(handle)
}


interface ComplexDetailActions {
  isActive: boolean
  addHoverHighlight: () => void
  removeHoverHighlight: () => void
  toggleDetail: () => void
}


export const useComplexDetail = (details: Detail[], PIN: number, type: ParcelDetailType): ComplexDetailActions => {
  const [highlightHandle, setHighlightHandle] = useState<number>(-1)
  const [isActive, setIsActive] = useState<boolean>(false)

  useEffect(() => {
    // handle remembering the highlight when switching between tabs
    const existingHandle = ComplexParcelDetailsLayer.getActiveHandle(PIN, type, details[0].label)
    if (existingHandle !== undefined && highlightHandle === -1) {
      setHighlightHandle(existingHandle)
      if (!isActive) setIsActive(true)
    } else if (existingHandle === undefined && highlightHandle !== -1){
      setHighlightHandle(-1)
      setIsActive(false)
    }
  }, [details, PIN, type, highlightHandle, isActive])

  const addHoverHighlight = useCallback(() => {
    if (!details || isActive || highlightHandle !== -1) return
    const handle = highlightDetail(details, PIN, type)
    setHighlightHandle(handle)
  }, [details, isActive, highlightHandle, PIN, type])

  const removeHoverHighlight = useCallback(() => {
    if (isActive) return
    unhighlightDetail(highlightHandle)
    setHighlightHandle(-1)
  }, [highlightHandle, isActive])

  const toggleDetail = useCallback(() => {
    if (isActive && highlightHandle !== -1) {
      unhighlightDetail(highlightHandle)
      setHighlightHandle(-1)
    } else if (!isActive && highlightHandle === -1) {
      const handle = highlightDetail(details, PIN, type)
      setHighlightHandle(handle)
    }
    setIsActive(!isActive)
  }, [isActive, details, highlightHandle, PIN, type])

  return {
    isActive,
    addHoverHighlight,
    removeHoverHighlight,
    toggleDetail,
  }
}